/**
 * StoreSwitcher — EGASI uchun "joriy do'kon" tanlagich (Navbar'da).
 *
 * EGASI bir nechta do'konga ega. Bu tanlagich orqali bitta do'kon yoki
 * "Hamma do'kon" tanlanadi. Tanlov `X-Active-Store-Id` header orqali serverga
 * yuboriladi va HAMMA ma'lumot (mahsulot, qarz, mijoz, statistika, kassa)
 * o'sha do'konga scope qilinadi (RLS). Faqat EGASI uchun ko'rinadi —
 * ADMIN/XODIM allaqachon o'z do'koniga qulflangan.
 *
 * Default: birinchi kirishda asosiy do'kon (isMain). localStorage'da saqlanadi.
 */
import { useQueryClient } from '@tanstack/react-query';
import { Check, ChevronDown, Store } from 'lucide-react';
import { useEffect } from 'react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useActiveStore } from '@/app/stores/active-store.store';
import { useCurrentUser } from '@/features/auth/auth.hooks';
import { useStores } from '@/features/stores/stores.hooks';
import { cn } from '@/shared/lib/cn';

export function StoreSwitcher(): React.ReactElement | null {
  const user = useCurrentUser();
  const isEgasi = user?.role === 'EGASI';
  const queryClient = useQueryClient();
  const activeStoreId = useActiveStore((s) => s.activeStoreId);
  const initialized = useActiveStore((s) => s.initialized);
  const setActiveStore = useActiveStore((s) => s.setActiveStore);
  const ensureDefault = useActiveStore((s) => s.ensureDefault);

  // EGASI bo'lmasa do'kon ro'yxatini ham so'ramaymiz (ADMIN ko'rmaydi)
  const { data } = useStores({ page: 1, limit: 200 }, { enabled: isEgasi });
  const stores = data?.data ?? [];

  // Default = asosiy do'kon (isMain) yoki birinchi; mavjud bo'lmagan tanlovni tuzatish
  useEffect(() => {
    if (!isEgasi || stores.length === 0) return;
    const fallback = stores.find((s) => s.isMain)?.id ?? stores[0]?.id ?? null;
    if (!initialized) {
      ensureDefault(fallback);
      void queryClient.invalidateQueries();
      return;
    }
    // Self-heal: tanlangan do'kon endi mavjud emas (o'chirilgan) → default'ga
    if (activeStoreId !== null && !stores.some((s) => s.id === activeStoreId)) {
      setActiveStore(fallback);
      void queryClient.invalidateQueries();
    }
  }, [isEgasi, stores, initialized, activeStoreId, ensureDefault, setActiveStore, queryClient]);

  if (!isEgasi) return null;

  const current = stores.find((s) => s.id === activeStoreId);
  const label = activeStoreId === null ? 'Hamma do\'kon' : (current?.name ?? 'Do\'kon');

  const select = (id: string | null): void => {
    if (id === activeStoreId) return;
    setActiveStore(id);
    void queryClient.invalidateQueries(); // hamma ma'lumot yangi do'konga qayta yuklanadi
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          title="Joriy do'kon"
          className={cn(
            'inline-flex h-9 max-w-[180px] items-center gap-1.5 rounded-lg border border-border bg-muted/30 px-2.5 text-sm font-medium text-foreground transition-colors',
            'hover:border-primary/40 hover:bg-primary/5 hover:text-primary',
          )}
        >
          <Store className="size-4 shrink-0" strokeWidth={1.75} />
          <span className="truncate">{label}</span>
          <ChevronDown className="size-4 shrink-0 opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-xs text-muted-foreground">Joriy do'kon</DropdownMenuLabel>
        <DropdownMenuItem
          onSelect={() => {
            select(null);
          }}
        >
          <Store className="size-4" />
          <span className="flex-1">Hamma do'kon</span>
          {activeStoreId === null && <Check className="size-4 text-primary" />}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {stores.map((s) => (
          <DropdownMenuItem
            key={s.id}
            onSelect={() => {
              select(s.id);
            }}
          >
            <Store className="size-4" />
            <span className="flex-1 truncate">
              {s.name}
              {s.isMain ? ' (asosiy)' : ''}
            </span>
            {activeStoreId === s.id && <Check className="size-4 text-primary" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
